import { Eye, MousePointer2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { EventStats } from "@vzlive/shared";
import { apiFetch } from "../../lib/api";
import { useAdmin } from "./AdminShell";

export function AdminStatsPage() {
  const { data } = useAdmin();
  const [eventId, setEventId] = useState(data.events[0]?.id ?? "");
  const [stats, setStats] = useState<EventStats | null>(null);

  useEffect(() => {
    if (!eventId) return;
    apiFetch<EventStats>(`/api/admin/events/${eventId}/stats`).then(setStats);
    const timer = window.setInterval(() => {
      apiFetch<EventStats>(`/api/admin/events/${eventId}/stats`).then(setStats);
    }, 20_000);
    return () => window.clearInterval(timer);
  }, [eventId]);

  const chart = useMemo(() => {
    const values = stats?.points ?? [];
    const width = 900;
    const height = 260;
    const max = Math.max(1, ...values.map((point) => point.peakOnline));
    const points = values.map((point, index) => ({
      x: values.length === 1 ? 0 : (index / (values.length - 1)) * width,
      y: height - (point.peakOnline / max) * (height - 30),
      ...point,
    }));
    return {
      points,
      line: points.map((point) => `${point.x},${point.y}`).join(" "),
      area: points.length
        ? `0,${height} ${points.map((point) => `${point.x},${point.y}`).join(" ")} ${width},${height}`
        : "",
      max,
    };
  }, [stats]);

  return (
    <div className="admin-page stats-page">
      <header className="admin-page-header">
        <div>
          <h1>Statistik</h1>
          <p>
            Datensparsame Presence-Werte – vollständig getrennt vom
            Produkt-Tracking.
          </p>
        </div>
        <select
          value={eventId}
          onChange={(event) => setEventId(event.target.value)}
        >
          {data.events.map((event) => (
            <option key={event.id} value={event.id}>
              {event.title}
            </option>
          ))}
        </select>
      </header>

      <section className="stats-cards">
        <article className="stats-card--live">
          <div>
            <strong>{stats?.onlineNow ?? "–"}</strong>
            <small>gerade online</small>
          </div>
          <i />
        </article>
        <article>
          <div>
            <strong>{stats?.totalUnique ?? "–"}</strong>
            <small>eindeutige Besucher</small>
          </div>
        </article>
        <article>
          <div>
            <strong>{chart.max || "–"}</strong>
            <small>höchster Live-Wert</small>
          </div>
        </article>
      </section>

      <section className="stats-chart">
        <header>
          <h2>Publikum über Zeit</h2>
          <span>
            <i /> Online-Sessions · letzte 24 Stunden
          </span>
        </header>
        {chart.points.length > 1 ? (
          <div className="stats-chart__plot">
            <svg
              viewBox="0 0 900 280"
              preserveAspectRatio="none"
              role="img"
              aria-label="Online-Sessions über Zeit"
            >
              <defs>
                <linearGradient id="statsArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="var(--brand)" stopOpacity=".28" />
                  <stop offset="1" stopColor="var(--brand)" stopOpacity="0" />
                </linearGradient>
              </defs>
              {[0, 1, 2, 3, 4].map((line) => (
                <line
                  key={line}
                  x1="0"
                  y1={line * 65}
                  x2="900"
                  y2={line * 65}
                />
              ))}
              <polygon points={chart.area} />
              <polyline points={chart.line} />
            </svg>
            <div className="stats-chart__labels">
              <span>
                {new Intl.DateTimeFormat("de", {
                  hour: "2-digit",
                  minute: "2-digit",
                }).format(new Date(chart.points[0]!.bucket))}
              </span>
              <span>jetzt</span>
            </div>
          </div>
        ) : (
          <div className="stats-chart__empty">
            <Eye />
            <p>Die Kurve entsteht, sobald die ersten Reader verbunden sind.</p>
          </div>
        )}
      </section>

      <aside className="stats-privacy">
        <MousePointer2 />
        <div>
          <strong>Presence, nicht Profiling.</strong>
          <p>
            VZ.live speichert nur gehashte, kurzlebige Besucher- und
            Session-IDs. Umami bleibt ein separates Produkt-Tracking und fließt
            nicht in diese Zahlen ein.
          </p>
        </div>
      </aside>
    </div>
  );
}
